import React, { useContext, useState, useEffect } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthContext } from "../../../context/AuthContext";
import { fetchWithAuth } from "../../../utils/api";
import Navbar from "../../ui/Navbar";
import Loader from "../../ui/Loader";
import StudentClasses from "./StudentClasses";
import StudentDashboard from "./StudentDashboard";
import PracticeTestHome from "./pageUI/PracticeTests/PracticeTestHome";
import PaymentModal from "../../Payment";
import Phase1ResultsPopup from "../../ui/Announcement/Phase1ResultsPopup";

const serverUrl = process.env.REACT_APP_SERVER_URL;

export default function StudentHome() {
  document.title = "GradeMe AI - AI Assistant for Teachers";
  const { isAuthenticated, primaryRole, user } = useContext(AuthContext);
  const location = useLocation();
  const [isRenewalModalOpen, setIsRenewalModalOpen] = useState(false);

  const instituteId =
    user?.instituteId ||
    (typeof user?.institute === "object"
      ? user?.institute?._id
      : user?.institute);

  const initialInstitute =
    user?.institute && typeof user.institute === "object"
      ? user.institute
      : null;

  const [institute, setInstitute] = useState(initialInstitute);
  const [loadingInstitute, setLoadingInstitute] = useState(
    Boolean(
      instituteId &&
        (!initialInstitute ||
          initialInstitute.standardizedTestTemplateIds === undefined)
    )
  );

  useEffect(() => {
    let isMounted = true;
    const fetchInstituteData = async () => {
      if (!instituteId) {
        setLoadingInstitute(false);
        return;
      }
      try {
        const response = await fetchWithAuth(
          `${serverUrl}/api/institute/get/${instituteId}`,
          {
            credentials: "include",
          }
        );
        if (!response || !response.ok) return;
        const data = await response.json();
        if (isMounted && data) {
          setInstitute(data);
        }
      } catch (err) {
        console.error("Failed to fetch institute data in StudentHome:", err);
      } finally {
        if (isMounted) {
          setLoadingInstitute(false);
        }
      }
    };

    if (
      instituteId &&
      (!initialInstitute ||
        initialInstitute.standardizedTestTemplateIds === undefined)
    ) {
      fetchInstituteData();
    }
    return () => {
      isMounted = false;
    };
  }, [instituteId, initialInstitute]);

  const isUdghosh =
    user?.category === "UDGHOSH" ||
    user?.institute?.category === "UDGHOSH" ||
    institute?.category === "UDGHOSH";
  const isEntranceExam = user?.category === "ENTRANCE_EXAM";

  const standardizedTestTemplateIds =
    institute?.standardizedTestTemplateIds ||
    user?.institute?.standardizedTestTemplateIds;

  const hasStandardizedTemplates = Boolean(
    standardizedTestTemplateIds &&
      Array.isArray(standardizedTestTemplateIds) &&
      standardizedTestTemplateIds.filter(Boolean).length > 0
  );

  const handleOpenRenewalModal = () => {
    setIsRenewalModalOpen(true);
  };

  if (!isAuthenticated || primaryRole !== "student") {
    return <Navigate to="/login" replace />;
  }

  if (loadingInstitute) {
    return <Loader />;
  }

  const pathParts = location.pathname.split("/");
  const currentPath = pathParts[pathParts.length - 1];
  let currentPage = isEntranceExam
    ? "Practice Zone"
    : isUdghosh
    ? "UNOSQ' 26"
    : "Classes";

  if (location.pathname.includes("practice-tests") && hasStandardizedTemplates) {
    currentPage = "Practice Zone";
  } else {
    switch (currentPath) {
      case "":
        currentPage = isEntranceExam
          ? "Practice Zone"
          : isUdghosh
          ? "UNOSQ' 26"
          : "Classes";
        break;
      case "dashboard":
        currentPage = isEntranceExam ? "Practice Zone" : "Dashboard";
        break;
      default:
        currentPage = isEntranceExam
          ? "Practice Zone"
          : isUdghosh
          ? "UNOSQ' 26"
          : "Classes";
    }
  }

  const navLinks = isEntranceExam
    ? [
        {
          id: "practice-tests",
          text: "Practice Zone",
          to: "/student/practice-tests",
          icon: "fa-solid fa-vial",
        },
      ]
    : [
        {
          id: "classes",
          text: isUdghosh ? "UNOSQ' 26" : "Classes",
          to: "/student",
          end: true,
          icon: "fa-solid fa-chalkboard-user",
        },
        ...(!isUdghosh
          ? [
              {
                id: "dashboard",
                text: "Dashboard",
                to: "/student/dashboard",
                icon: "fa-solid fa-chart-line",
              },
            ]
          : []),
        ...(hasStandardizedTemplates
          ? [
              {
                id: "practice-tests",
                text: "Practice Zone",
                to: "/student/practice-tests",
                icon: "fa-solid fa-vial",
              },
            ]
          : []),
      ];

  let breadcrumbs = [];
  switch (currentPage) {
    case "Classes":
    case "UNOSQ' 26":
      breadcrumbs = [
        { id: "home", name: "Home", path: "/student" },
        {
          id: "classes",
          name: isUdghosh ? "UNOSQ' 26" : "Classes",
          path: "/student",
          isLast: true,
        },
      ];
      break;
    case "Dashboard":
      breadcrumbs = [
        { id: "home", name: "Home", path: "/student" },
        {
          id: "dashboard",
          name: "Dashboard",
          path: "/student/dashboard",
          isLast: true,
        },
      ];
      break;
    case "Practice Zone":
      breadcrumbs = [
        { id: "home", name: "Home", path: "/student" },
        {
          id: "practice-tests",
          name: "Practice Zone",
          path: "/student/practice-tests",
          isLast: true,
        },
      ];
      break;
    default:
      breadcrumbs = [
        {
          id: "classes",
          name: isUdghosh ? "UNOSQ' 26" : "Classes",
          path: "/student",
          isLast: true,
        },
      ];
  }

  return (
    <>
      <Navbar links={navLinks} breadcrumbs={breadcrumbs} />
      <div className="student-content">
        <Routes>
          {isEntranceExam ? (
            <>
              <Route
                path="practice-tests/*"
                element={
                  <PracticeTestHome
                    onOpenRenewalModal={handleOpenRenewalModal}
                  />
                }
              />
              <Route
                path="*"
                element={<Navigate to="/student/practice-tests" replace />}
              />
            </>
          ) : (
            <>
              <Route
                index
                element={
                  <StudentClasses onOpenRenewalModal={handleOpenRenewalModal} />
                }
              />
              <Route path="dashboard" element={<StudentDashboard />} />
              {hasStandardizedTemplates && (
                <Route
                  path="practice-tests/*"
                  element={
                    <PracticeTestHome
                      onOpenRenewalModal={handleOpenRenewalModal}
                    />
                  }
                />
              )}
              <Route path="*" element={<Navigate to="/student" replace />} />
            </>
          )}
        </Routes>
      </div>
      <PaymentModal
        isOpen={isRenewalModalOpen}
        onClose={() => setIsRenewalModalOpen(false)}
      />
      {isUdghosh && <Phase1ResultsPopup />}
    </>
  );
}
